/*
* 此文件有别于同目录的 toggle 文件夹，toggle 文件夹可能是远古时代的配置
* toggle-config，是基于 前端灰度平台: https://wuji.woa.com/p/edit?appid=tencentdocs_toggle&schemaid=toggle 的 slide 的一套灰度配置
* 相关文档：https://doc.weixin.qq.com/doc/w3_AN0AxQbdAFw3GlcAUpjTCCaz1V7eA?scode=AJEAIQdfAAoBhGqIE6AGUAJAZaACo
*/
import { CalculatedToggle, DocsToggleConfig } from '@tencent/docs-scenario-component-docs-toggle-client';
import { statusReport007 } from '@src/slide/infrastructure/module/dependent/report/real-time-report/real-time';
import {
    Reporter007,
    Reporter007ActionType,
} from '@src/slide/infrastructure/module/dependent/report/real-time-report/constant';
import axios from 'axios';
import { getCookie } from 'steamer-browserutils';
const toggleInstance = DocsToggleConfig.getInstance();

/*
* 开关的种类
* 原则上，未来新增新的开关，都需要在 ToggleType上 声明，前端面向 ToggleType 开发
*/
export enum ToggleType {
    SLIDE_ADD_PAGE_RED_POINT = 'SLIDE_ADD_PAGE_RED_POINT',
    TDOCS_PICTURE_AUDIO_AND_VIDEO_SWITCH = 'TDOCS_PICTURE_AUDIO_AND_VIDEO_SWITCH',
    SCREENSHOT_COMPONENT = 'SCREENSHOT_COMPONENT',
    NEW_LOCAL_FILE_TRANSFER = 'NEW_LOCAL_FILE_TRANSFER',
    NEW_LOCAL_TO_SYNC_DOC = 'NEW_LOCAL_TO_SYNC_DOC',
    TEMPLATE_CHECK_BUTTON = 'TEMPLATE_CHECK_BUTTON',
    AI_REPLACE_SLIDE = 'AI_REPLACE_SLIDE',
    AI_CHART = 'AI_CHART',
    AI_FLOAT_BAR = 'AI_FLOAT_BAR',
}
/*
* 用于描述一个 Toggle 开关的基本索引信息
*/
interface ToggleInfo {
    id: string;
    name: string;
}
/*
* 开关集合
* 将开关索引在此注册。如果开关下线，需要将此map中对应的开关移除
*/
const toggleMap: Record<ToggleType, ToggleInfo> = {
    [ToggleType.SLIDE_ADD_PAGE_RED_POINT]: {
        id: '94',
        name: 'SLIDE_ADD_PAGE_RED_POINT',
    },
    [ToggleType.TDOCS_PICTURE_AUDIO_AND_VIDEO_SWITCH]: {
        id: '112',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: '腾讯文档图音视频开关',
    },
    [ToggleType.SCREENSHOT_COMPONENT]: {
        id: '156',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: '新版截图组件开关',
    },
    [ToggleType.NEW_LOCAL_FILE_TRANSFER]: {
        id: '180',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: '新版本本地文档转多文档入口开关',
    },
    [ToggleType.NEW_LOCAL_TO_SYNC_DOC]: {
        id: '187',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: '新版本本地文档转同步盘文档开关',
    },
    [ToggleType.TEMPLATE_CHECK_BUTTON]: {
        id: '188',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: '机器审核入口',
    },
    [ToggleType.AI_REPLACE_SLIDE]: {
        id: '199',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: 'AI 单页美化',
    },
    [ToggleType.AI_CHART]: {
        id: '201',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: 'AI 单页美化',
    },
    [ToggleType.AI_FLOAT_BAR]: {
        id: '202',
        // eslint-disable-next-line @tencent/i18next/no-literal-string
        name: 'AI 悬浮窗',
    },
};

enum ToggleSceneId {
    SLIDE = 15
}

/**
 * 通过接口获得开关状态（无缓存）
 * @param toggleType
 * @param fallback
 * @returns
 */
export const getToggleStatusByApi = async (toggleType: ToggleType, fallback?: boolean) => {
    const toggleInfo = toggleMap[toggleType];
    if (!toggleInfo) {
        window.log.error(`[getToggleStatusByType error] ${toggleType} not exist,  return fallback: ${fallback}`);
        return fallback;
    }
    const xsrf = getCookie('TOK');
    try {
        const sceneStatus = await axios.post(
            `https://docs.qq.com/api/frontedcfgreader/without_login/scenes_calc?xsrf=${xsrf}`,
            {
                scene_ids: [ToggleSceneId.SLIDE],
            },
            {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            },
        );
        return !!sceneStatus?.data?.result?.strategy_toggles?.find(item => item.id === toggleInfo.id)?.status;
    } catch (err) {
        return false;
    }
};

/*
* 通过 ToggleType 获取对应的开关状态
* 需要将对应的开关注册到toggleMap
* 业务获取开关会进行上报，可以在007平台查看实时上报（不同开关通过k7字段过滤）：
* https://007.woa.com/v2/data-query?appname=pp_trc_docx_status.slide_fe&k7=slide-chart-render-status&groupBy=k8
*/
export const getToggleStatusByType = (toggleType: ToggleType, fallback?: boolean) => {
    const toggleInfo = toggleMap[toggleType];
    if (!toggleInfo) {
        window.log.error(`[getToggleStatusByType error] ${toggleType} not exist,  return fallback: ${fallback}`);
        return fallback;
    }

    const toggleStatus = toggleInstance.getStatusByKey(toggleInfo.id, fallback);
    // 开光有实时性，这里通过007上报
    statusReport007(
        Reporter007ActionType.SLIDE_TOGGLE,
        // 开关打开上报成功，开关关闭上报失败
        toggleStatus ? Reporter007.SUCCESS : Reporter007.ERROR,
        toggleInfo.name,
    );

    return toggleStatus;
};

/*
* 通过 ToggleType 获取对应的开关
*/
export const getToggleByType = <T = CalculatedToggle>(toggleType: ToggleType, fallback?) => {
    const toggleInfo = toggleMap[toggleType];
    if (!toggleInfo) {
        window.log.error(`[getToggleStatusByType error] ${toggleType} not exist,  return fallback: ${fallback}`);
        return fallback;
    }

    return toggleInstance.getToggleByKey<T>(toggleInfo.id, fallback);
};
